import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import AdminLayout from '../AdminLayout';
import TipTapEditor from './components/TipTapEditor';
import ImageUploadZone from './components/ImageUploadZone';
import MagicButton from 'components/MagicButton/MagicButton';
import { useGetMeQuery } from 'store/services/user/meApi';
import {
  useGetArticleByIdQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useLazyCheckSlugAvailabilityQuery,
} from 'store/services/blog/blogApi';
import { BlogArticleStatus } from 'types/interfaces/blog';
import './ArticleEditorPage.css';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const ArticleEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: currentUser } = useGetMeQuery();
  const { data: existingArticle, isLoading: isLoadingArticle } = useGetArticleByIdQuery(id!, {
    skip: !isEditMode,
  });

  const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();
  const [deleteArticle, { isLoading: isDeleting }] = useDeleteArticleMutation();
  const [checkSlug] = useLazyCheckSlugAvailabilityQuery();

  // Form state
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [contentJson, setContentJson] = useState<any>({ type: 'doc', content: [] });
  const [contentHtml, setContentHtml] = useState('<p></p>');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [ogImageUrl, setOgImageUrl] = useState('');
  const [status, setStatus] = useState<BlogArticleStatus>('DRAFT');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSeoSection, setShowSeoSection] = useState(false);

  // Load existing article data
  useEffect(() => {
    if (existingArticle) {
      setTitle(existingArticle.title);
      setSubtitle(existingArticle.subtitle || '');
      setSlug(existingArticle.slug);
      setHeroImageUrl(existingArticle.heroImageUrl || '');

      // Parse contentJson recursively to handle double/triple stringification from backend
      let parsedContent = existingArticle.contentJson;
      try {
        // Keep parsing while it's a string (handles multiple levels of stringification)
        while (typeof parsedContent === 'string') {
          parsedContent = JSON.parse(parsedContent);
        }

        // Validate it's a proper TipTap document structure with type: 'doc'
        if (!parsedContent || typeof parsedContent !== 'object' || parsedContent.type !== 'doc') {
          // If it's a valid node but missing the doc wrapper, wrap it
          if (parsedContent && parsedContent.type) {
            parsedContent = { type: 'doc', content: [parsedContent] };
          } else {
            parsedContent = { type: 'doc', content: [] };
          }
        }

        setContentJson(parsedContent);
      } catch (error) {
        console.error('Error parsing article content:', error);
        setContentJson({ type: 'doc', content: [] });
      }

      setContentHtml(existingArticle.contentHtml);
      setMetaTitle(existingArticle.metaTitle || '');
      setMetaDescription(existingArticle.metaDescription || '');
      setOgImageUrl(existingArticle.ogImageUrl || '');
      setStatus(existingArticle.status);
    }
  }, [existingArticle]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditMode && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setSlug(generatedSlug);
    }
  }, [title, isEditMode]);

  // Validate slug availability
  const validateSlug = async () => {
    if (!slug) {
      setSlugError('Slug is required');
      return false;
    }

    // Skip validation if editing and slug hasn't changed
    if (isEditMode && existingArticle && slug === existingArticle.slug) {
      setSlugError('');
      return true;
    }

    try {
      const result = await checkSlug(slug).unwrap();
      if (!result.data.available) {
        setSlugError('This slug is already taken');
        return false;
      }
      setSlugError('');
      return true;
    } catch (error) {
      console.error('Slug validation failed:', error);
      return true; // Continue anyway if validation fails
    }
  };

  const handleEditorChange = (json: any, html: string) => {
    setContentJson(json);
    setContentHtml(html);
  };

  const handleSave = async (saveStatus: BlogArticleStatus) => {
    if (!currentUser) {
      alert('You must be logged in to save articles');
      return;
    }

    if (!title.trim()) {
      alert('Title is required');
      return;
    }

    const isSlugValid = await validateSlug();
    if (!isSlugValid) {
      alert('Please fix the slug error before saving');
      return;
    }

    if (saveStatus === 'PUBLISHED' && !heroImageUrl) {
      alert('Hero image is required for published articles');
      return;
    }

    // Empty TipTap document structure for fallback
    const emptyTipTapDoc = { type: 'doc', content: [] };

    // Validate required fields
    if (!contentJson && !contentHtml) {
      alert('Article content is required. Please add some content before saving.');
      return;
    }

    // Prepare article data with proper formatting
    const articleData: any = {
      title: title.trim(),
      subtitle: subtitle.trim() || undefined,
      slug: slug.trim(),
      heroImageUrl: heroImageUrl || undefined,
      contentJson: JSON.stringify(contentJson || emptyTipTapDoc), // Backend expects stringified JSON
      contentHtml: contentHtml || '<p></p>', // Ensure never empty
      status: saveStatus,
      authorId: Number(currentUser.id), // Ensure it's a number
      metaTitle: metaTitle.trim() || undefined,
      metaDescription: metaDescription.trim() || undefined,
      ogImageUrl: ogImageUrl || heroImageUrl || undefined,
    };

    // Log request for debugging
    console.log('📝 Saving article - DETAILED DEBUG:', {
      isEditMode,
      articleId: id,
      title: articleData.title,
      contentJson: articleData.contentJson,
      contentJsonType: typeof articleData.contentJson,
      contentHtml: articleData.contentHtml,
      contentHtmlLength: articleData.contentHtml.length,
      authorId: articleData.authorId,
      authorIdType: typeof articleData.authorId,
      status: articleData.status,
      fullPayload: articleData,
    });

    try {
      if (isEditMode && id) {
        await updateArticle({ id, data: articleData }).unwrap();
        alert('Article updated successfully!');
      } else {
        const result = await createArticle(articleData).unwrap();
        alert('Article created successfully!');
        navigate(`/admin/blog/articles/${result.id}/edit`);
      }
    } catch (error: any) {
      console.error('❌ Failed to save article:', {
        error,
        errorData: error?.data,
        errorMessage: error?.data?.message,
        errorDetails: error?.data?.errors,
        status: error?.status,
      });

      // Show detailed error message
      const errorMsg = error?.data?.errors
        ? `Validation errors:\n${JSON.stringify(error.data.errors, null, 2)}`
        : error?.data?.message || 'Failed to save article. Please try again.';

      alert(errorMsg);
    }
  };

  const handleDelete = async () => {
    if (id) {
      try {
        await deleteArticle(id).unwrap();
        alert('Article deleted successfully');
        navigate('/admin/blog/articles');
      } catch (error) {
        console.error('Failed to delete article:', error);
        alert('Failed to delete article. Please try again.');
      }
    }
  };

  if (isEditMode && isLoadingArticle) {
    return (
      <AdminLayout title="Admin Panel - Loading..." currentPath="/admin/blog/articles">
        <div className="article-editor-loading">Loading article...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={`Admin Panel - ${isEditMode ? 'Edit' : 'New'} Article`}
      currentPath="/admin/blog/articles"
    >
      <div className="article-editor-page">
        {/* Header */}
        <div className="article-editor-header">
          <div className="header-left">
            <button className="back-button" onClick={() => navigate('/admin/blog/articles')}>
              ← Back to Articles
            </button>
            <h2 className="article-editor-title">
              {isEditMode ? 'Edit Article' : 'Create New Article'}
            </h2>
          </div>
          <div className="header-actions">
            <MagicButton
              label="Save Draft"
              onPress={() => handleSave('DRAFT')}
              color="grey"
              isDisabled={isCreating || isUpdating}
            />
            <MagicButton
              label={isEditMode ? 'Update & Publish' : 'Publish'}
              onPress={() => handleSave('PUBLISHED')}
              color="purple"
              isDisabled={isCreating || isUpdating}
            />
            {isEditMode && (
              <button
                className="delete-article-button"
                onClick={() => setShowDeleteModal(true)}
                disabled={isDeleting}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Metadata Fields */}
        <div className="article-metadata">
          <div className="form-row">
            <div className="form-field full-width">
              <label>
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter article title"
                maxLength={100}
              />
              <span className="field-hint">{title.length}/100 characters</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field full-width">
              <label>Subtitle</label>
              <input
                type="text"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Optional subtitle or summary"
                maxLength={200}
              />
              <span className="field-hint">{subtitle.length}/200 characters</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>
                Slug <span className="required">*</span>
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                onBlur={validateSlug}
                placeholder="article-url-slug"
              />
              {slugError && <span className="field-error">{slugError}</span>}
              <span className="field-hint">URL: /articles/{slug || 'your-slug'}</span>
            </div>
            <div className="form-field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as BlogArticleStatus)}>
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          </div>

          {/* Hero Image */}
          <ImageUploadZone
            label="Hero Image"
            description="Recommended size: 1200x600px. Required for published articles."
            currentImageUrl={heroImageUrl}
            onImageUploaded={setHeroImageUrl}
          />
        </div>

        {/* Content Editor */}
        <div className="article-content-section">
          <label className="section-label">Article Content</label>
          <TipTapEditor content={contentJson} onChange={handleEditorChange} />
        </div>

        {/* SEO Section (Collapsible) */}
        <div className="article-seo-section">
          <button
            className="seo-section-toggle"
            onClick={() => setShowSeoSection(!showSeoSection)}
          >
            <span>SEO & Metadata</span>
            <span className="toggle-arrow">{showSeoSection ? '▼' : '▶'}</span>
          </button>

          {showSeoSection && (
            <div className="seo-fields">
              <div className="form-field full-width">
                <label>Meta Title</label>
                <input
                  type="text"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder={`${title} - TradeMagic`}
                  maxLength={60}
                />
                <span className="field-hint">
                  {metaTitle.length}/60 characters (defaults to article title)
                </span>
              </div>

              <div className="form-field full-width">
                <label>Meta Description</label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Brief description for search engines"
                  maxLength={160}
                  rows={3}
                />
                <span className="field-hint">{metaDescription.length}/160 characters</span>
              </div>

              <div className="form-field full-width">
                <label>OG Image URL</label>
                <input
                  type="url"
                  value={ogImageUrl}
                  onChange={(e) => setOgImageUrl(e.target.value)}
                  placeholder={heroImageUrl || 'https://...'}
                />
                <span className="field-hint">Defaults to hero image if not specified</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="article-editor-footer">
          <MagicButton
            label="Cancel"
            onPress={() => navigate('/admin/blog/articles')}
            color="grey"
          />
          <div className="footer-save-actions">
            <MagicButton
              label="Save Draft"
              onPress={() => handleSave('DRAFT')}
              color="grey"
              isDisabled={isCreating || isUpdating}
            />
            <MagicButton
              label={isEditMode ? 'Update & Publish' : 'Publish'}
              onPress={() => handleSave('PUBLISHED')}
              color="purple"
              isDisabled={isCreating || isUpdating}
            />
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          articleTitle={title}
          isDeleting={isDeleting}
        />
      </div>
    </AdminLayout>
  );
};

export default ArticleEditorPage;
